import { toast } from 'sonner'

import { useNavigate } from 'react-router-dom'

import { useCallback } from 'react'

import { useCart } from '@/contexts/CartContext'
import { useSession } from '@/contexts/SessionContext'
import { CustomerModel, ProductModel } from '@/http/models'
import { configService } from '@/services/ConfigService'
import { findRef } from '@/db/repositories/orders'
import { askItemDetails } from '@/services/itemPrompt'
import { formatDateTime, formatMoney } from '@/utils'
import { scaleFormats } from '@/services/devices/setup'
import { isRef, parseScaleLabel } from '@/services/scan'
import { PosModel } from '@/types'

/** `3*8941230000001` rings up three. */
const parseQuantity = (input: string) => {
  const m = input.match(/^(\d{1,3})\s*[*x]\s*(.+)$/i)
  return m ? { quantity: Number(m[1]), code: m[2].trim() } : { quantity: 1, code: input }
}

/**
 * Every way a product reaches the cart (tap, scan, Enter in search) goes through here, so the stock checks and the
 * feedback are the same everywhere.
 */
export const useAddToCart = () => {
  const cart = useCart()
  const navigate = useNavigate()
  const { can } = useSession()

  /**
   * `quantity` omitted: one, or asked for when the product is sold by unit. A variable or zero price is asked for the
   * first time the product enters the cart; later adds keep that line's price.
   */
  const add = useCallback(
    async (product: PosModel.Product, quantity?: number) => {
      const line = cart.lines.find((l) => l.product._id === product._id)
      const needs = {
        price: !line && (product.price_type === 'variable' || product.price <= 0),
        quantity: quantity === undefined && !!product.sold_by_unit,
      }
      let item = line?.product ?? product
      let qty = quantity ?? 1
      if (needs.price || needs.quantity) {
        const details = await askItemDetails(product, needs)
        if (!details) return
        // A price the cashier typed in is the price, whatever the customer's price book says.
        if (details.price !== undefined) item = { ...product, price: details.price, book_prices: undefined }
        if (details.quantity !== undefined) qty = details.quantity
      }
      // The sale goes ahead below zero stock; the cashier is told.
      const inCart = line?.quantity ?? 0
      if (inCart + qty > product.stock) toast.warning(`${product.name}: ${Math.max(product.stock, 0)} in stock, selling past it`)
      cart.add(item, qty)
    },
    [cart]
  )

  /**
   * A scan or Enter in search (POS.md §18.8): weight-scale label, product barcode, customer card, receipt ref; if
   * none matches, the single visible search match. Returns true when the input was used.
   */
  const addByCode = useCallback(
    async (input: string, fallback?: PosModel.Product[]) => {
      const { quantity, code } = parseQuantity(input)
      const device = !configService.isDemo()

      const label = device ? parseScaleLabel(code, scaleFormats()) : null
      if (label) {
        const res = await ProductModel.getByPlu(label.plu)
        if (res.success && res.data) {
          if (label.kg !== undefined) {
            add(res.data, label.kg)
            return true
          }
          // A price label: the scale priced it at the unit price, so the weight is price ÷ unit price.
          if (res.data.price > 0) {
            add(res.data, Math.round(((label.price ?? 0) / res.data.price) * 1000) / 1000)
            return true
          }
          toast.error(`${res.data.name} has no unit price to read the label with`)
          return false
        }
        toast.error(`No product with PLU ${label.plu}`)
        return false
      }

      const res = await ProductModel.getByBarcode(code)
      if (res.success && res.data) {
        add(res.data, quantity)
        return true
      }

      if (device) {
        const card = await CustomerModel.getByBarcode(code)
        if (card.success && card.data) {
          cart.setCustomer(card.data)
          toast.success(`${card.data.name} added to the sale`)
          return true
        }
      }

      if (device && isRef(code)) {
        const found = await findRef(code)
        if (found?.kind === 'repayment') {
          toast.info(`Credit repayment ${code}`, {
            description: `${formatMoney(found.amount)}${found.customer ? ` from ${found.customer}` : ''}${found.created_at ? ` · ${formatDateTime(found.created_at)}` : ''}`,
          })
          return true
        }
        if (found && can('pos:list')) {
          // A return slip opens its sale, which lists the returns.
          navigate(`/orders?search=${encodeURIComponent(found.order_ref)}`)
          return true
        }
      }

      if (fallback?.length === 1) {
        add(fallback[0], quantity)
        return true
      }
      toast.error(fallback?.length ? `${fallback.length} products match “${code}” — pick one` : `Nothing matches “${code}”`)
      return false
    },
    [add, cart, can, navigate]
  )

  return { add, addByCode }
}
